import { motion } from "framer-motion";
import { ArrowUpRight, BadgeCheck, Gift, HeartHandshake, Zap } from "lucide-react";
import { gofundmeUrl } from "../i18n";

const EASE = [0.16, 1, 0.3, 1];
const ICONS = [Zap, BadgeCheck, HeartHandshake, Gift];

export default function Funding({ t, locale }) {
  return (
    <section id="funding" data-testid="funding-section" className="relative border-t border-black/10">
      <div className="mx-auto max-w-7xl px-6 py-28 md:py-40">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: EASE }}
          className="border border-black/15 bg-surface"
        >
          <div className="grid gap-12 p-10 md:grid-cols-2 md:p-16 lg:gap-20">
            <div>
              <p data-testid="funding-overline" className="mb-4 font-display text-xs font-bold uppercase tracking-[0.25em] text-flame md:text-sm">
                {t.overline}
              </p>
              <h2 data-testid="funding-heading" className="font-display text-4xl font-bold tracking-tight text-bone sm:text-5xl">
                {t.titleA}{" "}
                <span className="font-editorial italic text-flame">{t.titleB}</span>
              </h2>
              <p data-testid="funding-text" className="mt-8 text-base leading-relaxed text-ash md:text-lg">
                {t.text}
              </p>
            </div>

            <div className="flex flex-col justify-between gap-10">
              <div>
                <h3 data-testid="funding-benefits-title" className="mb-6 font-display text-sm font-bold uppercase tracking-[0.2em] text-bone">
                  {t.benefitsTitle}
                </h3>
                <ul className="space-y-5">
                  {t.benefits.map((b, i) => {
                    const Icon = ICONS[i % ICONS.length];
                    return (
                      <motion.li
                        key={i}
                        data-testid={`funding-benefit-${i}`}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
                        className="flex items-start gap-4 text-base text-ash md:text-lg"
                      >
                        <Icon size={20} className="mt-1 shrink-0 text-flame" />
                        <span>{b}</span>
                      </motion.li>
                    );
                  })}
                </ul>
              </div>

              <div>
                <motion.a
                  data-testid="gofundme-cta-btn"
                  href={gofundmeUrl(locale)}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center gap-3 rounded-full bg-flame px-10 py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-flame-hover"
                >
                  {t.cta}
                  <ArrowUpRight size={18} />
                </motion.a>
                <p data-testid="funding-note" className="mt-4 text-sm text-ash/70">
                  {t.note}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
